import { Button, Typography } from '@mui/material';
import useDrawer from 'hooks/useDrawer/useDrawer';
import { kebabCase } from 'lodash';
import React from 'react';
import styled from 'styled-components';
import Loader from 'ui/atoms/Loader/Loader';
import ComponentWrapper from 'ui/templates/ComponentWrapper/ComponentWrapper';

export interface Props {
  children: React.ReactNode;
  title: string;
  headerSideText?: React.ReactNode;
  confirmButtonText?: string;
  disableConfirmButton?: boolean;
  closeButton?: boolean;
  closeButtonText?: string;
  isLoading?: boolean;
  handleCloseDrawer?: () => void;
  id: string;
  headerSideTextOnClick?: () => void;
  onSubmit: () => void;
}

const StyledTitleContainer = styled.div`
  display: flex;
  max-width: 23rem;
  width: 100%;
  margin-bottom: 1.5rem;
  justify-content: space-between;
  align-items: center;
`;

const StyledEditContainer = styled.div`
  display: flex;
  align-items: center;

  svg {
    margin-left: 0.5rem;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 22.9375rem;
  height: 100%;
  overflow-y: auto;
  row-gap: 1rem;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const ButtonsSection = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;

  button:first-child {
    display: flex;
    justify-content: center;
  }

  button:last-child {
    display: flex;
    justify-content: center;
  }
`;

const StyledForm = styled.form`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const DrawerTemplate: React.FC<React.PropsWithChildren<Props>> = ({
  children,
  title,
  headerSideText,
  headerSideTextOnClick,
  confirmButtonText,
  disableConfirmButton = false,
  closeButton = false,
  closeButtonText,
  isLoading = false,
  handleCloseDrawer,
  id,
  onSubmit
}) => {
  const { hideDrawer } = useDrawer();
  const closeDrawer = () => {
    if (handleCloseDrawer) handleCloseDrawer();
    hideDrawer();
  };

  return (
    <ComponentWrapper flexDirection="column" justifyContent="space-between" height="100%" id={id}>
      <StyledTitleContainer>
        <Typography variant="h6" id={kebabCase(`drawer-${title}`)}>
          {title}
        </Typography>
        <StyledEditContainer onClick={headerSideTextOnClick}>{headerSideText}</StyledEditContainer>
      </StyledTitleContainer>
      {isLoading ? (
        <ComponentWrapper justifyContent="center" alignItems="center" width="22.9375rem" height="100%">
          <Loader size={100} />
        </ComponentWrapper>
      ) : (
        <StyledForm onSubmit={onSubmit}>
          <Container>{children}</Container>
          {(confirmButtonText || closeButtonText) && (
            <ButtonsSection>
              {closeButton && (
                <Button fullWidth size="large" color="inherit" onClick={closeDrawer} id="close-button">
                  {closeButtonText}
                </Button>
              )}
              <Button
                type="submit"
                color="primary"
                fullWidth
                size="large"
                variant="contained"
                id="submit-button"
                disabled={disableConfirmButton}
              >
                {confirmButtonText}
              </Button>
            </ButtonsSection>
          )}
        </StyledForm>
      )}
    </ComponentWrapper>
  );
};

export default DrawerTemplate;
